// Разбор вставленного из Excel листа остатков: кто, за что, сколько.
//
// ## Два вида листа, и оба у заказчика настоящие
//
// «По строкам» — у каждого долга своя строка: «Карастелев Д. · Форма · 3 600». Так пишут список
// руками. «По столбцам» — у каждого вида долга свой столбец, как в его ведомости («Билеты»,
// «Обучение», «Форма» стоят там отдельными колонками), первая строка — заголовки. Так выглядит
// лист, собранный из книги: 15.09.2026 заказчик сам предложил «отдельно лист с остатками» при
// закрытии августа.
//
// Какой из двух перед нами, решает первая строка: если в ней есть «ФИО» или названия видов
// долга и нет ни одной суммы — это заголовки.
//
// ## Чего разбор НЕ делает
//
// Не ищет людей: фамилию сравнивает с реестром сервер, у него все люди разом и одно правило для
// однофамильцев. Не угадывает сумму: строка, где чисел два и не понять, какое остаток,
// попадает в «не разобрано» целиком — с номером, чтобы её нашли в своём листе. Суммы
// разбирает общий разбор денег кабинета, второй разборщик рядом с ним однажды разошёлся бы с
// первым на пробеле между разрядами.

import { parseRublesToKopecks } from '@/shared/money/parse';

/** Вид долга из справочника сервера: по его названию узнаются слова листа. */
export interface ВидДолга {
  code: string;
  title: string;
}

/** Строка, готовая уйти на сервер. */
export interface СтрокаЛиста {
  /** Номер строки во вставке, с единицы: по нему строку находят в своём листе. */
  номер: number;
  имя: string;
  вид: string;
  /** Копейки. */
  сумма: number;
  /** Как строка выглядит для человека: уходит в описание долга. */
  текст: string;
}

export interface НеРазобрано {
  номер: number;
  текст: string;
  причина: string;
}

export interface РазборЛиста {
  строки: СтрокаЛиста[];
  неРазобрано: НеРазобрано[];
  /** Как понят лист: человеку это видно, и по нему видно, если понят не так. */
  вид: 'по строкам' | 'по столбцам';
}

/** Слова заголовка, которые означают «здесь фамилия». */
const СТОЛБЕЦ_ИМЕНИ = /^(фио|ф\.?и\.?о\.?|фамилия.*|сотрудник|работник|охранник|кто)$/;

/**
 * Итоговые строки листа — не люди. Граница слова задана явно: `\b` в JS понимает только
 * латиницу, и «Итого» с ним не узнавалось — итог листа уходил в долг человеку по фамилии
 * «Итого».
 */
const ИТОГ = /^(итого|всего|итог)(?![а-яa-z])/;

/**
 * Слова, которых нет в названиях видов, но которые значат их без сомнений. Список нарочно
 * короткий: каждое слово здесь — это деньги, которые лягут не в тот вид долга.
 */
const ЕЩЁ_СЛОВА: ReadonlyArray<readonly [string, string]> = [
  // «Цены на спецодежду» — так этот справочник называет сама программа.
  ['спецодежд', 'uniform'],
  // «Неудержанный аванс» в листе пишут одним словом.
  ['аванс', 'unconfirmed_advance'],
];

function нормально(текст: string): string {
  return текст.replace(/ё/gi, 'е').trim().toLowerCase();
}

/**
 * Вид долга по слову из листа: «Билеты» → билет, «форма» → форма. Сравнение по началу слова:
 * в листе пишут и «Билет», и «Билеты», и «Билеты (остаток)».
 */
export function видПоСлову(слово: string, виды: readonly ВидДолга[]): string | null {
  const искомое = нормально(слово)
    .replace(/[^a-zа-я ]/g, ' ')
    .trim();
  if (искомое.length < 3) {
    return null;
  }
  for (const вид of виды) {
    const название = нормально(вид.title);
    // Основа — первые пять букв названия: «билет», «обуче», «форма», «удост». Короче пяти
    // основа начинает совпадать с чужими словами.
    const основа = название.slice(0, Math.min(5, название.length));
    if (искомое.startsWith(основа) || искомое === название) {
      return вид.code;
    }
  }
  for (const [начало, код] of ЕЩЁ_СЛОВА) {
    if (искомое.startsWith(начало) && виды.some((вид) => вид.code === код)) {
      return код;
    }
  }
  return null;
}

function ячейки(строка: string): string[] {
  // Табуляция — то, чем Excel разделяет колонки в буфере. Точка с запятой и два пробела подряд
  // — то, чем разделяют, набирая руками. Одиночный пробел НЕ разделитель: он внутри фамилии с
  // инициалами и внутри суммы «3 600».
  return строка.split(/\t|;|\s{2,}/).map((кусок) => кусок.trim());
}

function сумма(текст: string): number | null {
  // «3 600 ₽», «3600,00 руб.» — знак валюты и слово отбрасываются до разбора.
  const чистая = текст.replace(/(₽|руб\.?|р\.)/gi, '').trim();
  if (чистая === '') {
    return null;
  }
  const разбор = parseRublesToKopecks(чистая);
  return разбор.ok ? разбор.value : null;
}

/** В ячейке нет денег: пусто, прочерк или ноль. У вида долга в этой строке остатка нет. */
function пустоДенег(текст: string): boolean {
  const чистая = текст.replace(/(₽|руб\.?|р\.)/gi, '').trim();
  return чистая === '' || /^[-–—]+$/.test(чистая) || сумма(чистая) === 0;
}

function естьБуква(текст: string): boolean {
  return /[a-zа-яё]/i.test(текст);
}

export function разобратьЛист(
  текст: string,
  виды: readonly ВидДолга[],
  видПоУмолчанию: string | null,
): РазборЛиста {
  const строкиТекста = текст
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((строка, индекс) => ({ номер: индекс + 1, строка }))
    .filter(({ строка }) => строка.trim() !== '');

  const первая = строкиТекста[0];
  if (первая === undefined) {
    return { строки: [], неРазобрано: [], вид: 'по строкам' };
  }

  const заголовки = ячейки(первая.строка);
  const этоЗаголовки =
    !заголовки.some((ячейка) => сумма(ячейка) !== null) &&
    заголовки.some(
      (ячейка) => СТОЛБЕЦ_ИМЕНИ.test(нормально(ячейка)) || видПоСлову(ячейка, виды) !== null,
    );

  if (этоЗаголовки) {
    const столбцыВидов = заголовки
      .map((ячейка, индекс) => ({ индекс, код: видПоСлову(ячейка, виды), название: ячейка }))
      .filter(
        (столбец): столбец is { индекс: number; код: string; название: string } =>
          столбец.код !== null,
      );
    if (столбцыВидов.length > 0) {
      const имяВ = Math.max(
        0,
        заголовки.findIndex((ячейка) => СТОЛБЕЦ_ИМЕНИ.test(нормально(ячейка))),
      );
      return поСтолбцам(строкиТекста.slice(1), столбцыВидов, имяВ);
    }
    // Заголовки есть, а столбцов видов нет — значит лист «по строкам» с шапкой: «ФИО, за
    // что, сумма». Шапку пропускаем, дальше обычный разбор.
    return поСтрокам(строкиТекста.slice(1), виды, видПоУмолчанию);
  }
  return поСтрокам(строкиТекста, виды, видПоУмолчанию);
}

function поСтолбцам(
  строки: readonly { номер: number; строка: string }[],
  столбцы: readonly { индекс: number; код: string; название: string }[],
  имяВ: number,
): РазборЛиста {
  const готовые: СтрокаЛиста[] = [];
  const неРазобрано: НеРазобрано[] = [];
  for (const { номер, строка } of строки) {
    const куски = ячейки(строка);
    const имя = куски[имяВ] ?? '';
    if (ИТОГ.test(нормально(имя))) {
      continue;
    }
    if (!естьБуква(имя)) {
      неРазобрано.push({ номер, текст: строка, причина: 'нет фамилии в столбце ФИО' });
      continue;
    }
    for (const столбец of столбцы) {
      const ячейка = куски[столбец.индекс] ?? '';
      if (пустоДенег(ячейка)) {
        continue;
      }
      const копейки = сумма(ячейка);
      if (копейки === null) {
        неРазобрано.push({
          номер,
          текст: строка,
          причина: `в столбце «${столбец.название}» не сумма: «${ячейка}»`,
        });
        continue;
      }
      готовые.push({
        номер,
        имя,
        вид: столбец.код,
        сумма: копейки,
        текст: `${имя} · ${столбец.название} · ${ячейка}`,
      });
    }
  }
  return { строки: готовые, неРазобрано, вид: 'по столбцам' };
}

function поСтрокам(
  строки: readonly { номер: number; строка: string }[],
  виды: readonly ВидДолга[],
  видПоУмолчанию: string | null,
): РазборЛиста {
  const готовые: СтрокаЛиста[] = [];
  const неРазобрано: НеРазобрано[] = [];
  for (const { номер, строка } of строки) {
    const [имя = '', ...остальные] = ячейки(строка).filter((кусок) => кусок !== '');
    if (ИТОГ.test(нормально(имя))) {
      continue;
    }
    if (!естьБуква(имя)) {
      неРазобрано.push({ номер, текст: строка, причина: 'первым столбцом должна идти фамилия' });
      continue;
    }
    const суммы = остальные.filter((кусок) => сумма(кусок) !== null);
    const слова = остальные.filter((кусок) => сумма(кусок) === null);
    if (суммы.length === 0) {
      неРазобрано.push({ номер, текст: строка, причина: 'нет суммы остатка' });
      continue;
    }
    if (суммы.length > 1) {
      неРазобрано.push({
        номер,
        текст: строка,
        причина:
          'в строке несколько чисел — не понять, какое из них остаток. Оставьте одно или добавьте первой строкой заголовки столбцов',
      });
      continue;
    }
    const копейки = сумма(суммы[0] ?? '') ?? 0;
    if (копейки === 0) {
      неРазобрано.push({ номер, текст: строка, причина: 'остаток ноль — переносить нечего' });
      continue;
    }
    const названный = слова.map((слово) => видПоСлову(слово, виды)).find((код) => код !== null);
    const вид = названный ?? (слова.length === 0 ? видПоУмолчанию : null);
    if (вид === null || вид === undefined) {
      неРазобрано.push({
        номер,
        текст: строка,
        причина:
          слова.length > 0
            ? `вид долга «${слова.join(' ')}» не узнан: напишите, как в программе, — ${виды
                .slice(0, 4)
                .map((вид) => вид.title.toLowerCase())
                .join(', ')}…`
            : 'не сказано, за что долг: допишите столбец «за что» или выберите вид ниже',
      });
      continue;
    }
    const названиеВида = виды.find((кандидат) => кандидат.code === вид)?.title ?? вид;
    готовые.push({
      номер,
      имя,
      вид,
      сумма: копейки,
      текст: `${имя} · ${названиеВида} · ${суммы[0] ?? ''}`,
    });
  }
  return { строки: готовые, неРазобрано, вид: 'по строкам' };
}
